import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { ShieldCheck, Download, Loader2 } from 'lucide-react';
import { auditLogsApi } from '../api/services';
import type { AuditLogEntry } from '../types/models';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

// ---------------------------------------------------------------------------
// AuditLogPage — Phase 3 task 3.9.
// Executive-only page exposing the immutable audit log with date range,
// action / entity filters, and a CSV export.
// ---------------------------------------------------------------------------

const ALL = '__all__';

function formatTs(ts: string): string {
  try { return format(new Date(ts), 'd MMM yyyy HH:mm:ss'); } catch { return ts; }
}

export default function AuditLogPage() {
  const today = format(new Date(), 'yyyy-MM-dd');
  const sevenDaysAgo = format(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
  const [from, setFrom] = useState(sevenDaysAgo);
  const [to, setTo] = useState(today);
  const [actionFilter, setActionFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await auditLogsApi.getAll({
        from: new Date(from).toISOString(),
        to: new Date(new Date(to).getTime() + 24 * 60 * 60 * 1000 - 1).toISOString(),
        action: actionFilter || undefined,
        entityType: entityFilter || undefined,
        limit: 500,
      });
      setLogs(data);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not load audit log.');
    } finally {
      setLoading(false);
    }
  }, [from, to, actionFilter, entityFilter]);

  useEffect(() => { load(); }, [load]);

  const entityTypes = useMemo(
    () => Array.from(new Set(logs.map((l) => l.entityType).filter(Boolean))) as string[],
    [logs],
  );
  const actions = useMemo(
    () => Array.from(new Set(logs.map((l) => l.action).filter(Boolean))) as string[],
    [logs],
  );

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await auditLogsApi.exportCsv({
        from: new Date(from).toISOString(),
        to: new Date(new Date(to).getTime() + 24 * 60 * 60 * 1000 - 1).toISOString(),
        action: actionFilter || undefined,
        entityType: entityFilter || undefined,
      });
      const blob = res.data as Blob;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${from}-${to}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 0);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not export CSV.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1200px] p-4 md:p-6">
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-7 w-7 text-primary" />
          <h1 className="text-2xl font-semibold tracking-tight">Audit log</h1>
        </div>
        <Button
          variant="outline"
          disabled={exporting || logs.length === 0}
          onClick={handleExport}
          className="gap-1.5"
        >
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Export CSV
        </Button>
      </div>
      <p className="mb-6 text-sm text-muted-foreground">
        Immutable record of significant writes across work items, plans, decisions, drivers, projects,
        KPIs, and out-of-office windows. Exportable for compliance.
      </p>

      <Card className="mb-6">
        <CardContent className="py-3">
          <div className="flex flex-col gap-3 md:flex-row md:items-end">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="audit-from">From</Label>
              <Input
                id="audit-from"
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="w-[160px]"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="audit-to">To</Label>
              <Input
                id="audit-to"
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="w-[160px]"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Action</Label>
              <Select
                value={actionFilter || ALL}
                onValueChange={(v) => setActionFilter(v === ALL ? '' : v)}
              >
                <SelectTrigger className="w-[220px]">
                  <SelectValue placeholder="All actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All actions</SelectItem>
                  {actions.map((a) => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Entity</Label>
              <Select
                value={entityFilter || ALL}
                onValueChange={(v) => setEntityFilter(v === ALL ? '' : v)}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="All entities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>All entities</SelectItem>
                  {entityTypes.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="error" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading && (
        <div className="py-10 text-center">
          <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {!loading && logs.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-base font-semibold text-muted-foreground">
              No audit entries in this window.
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              Widen the date range or change the filters to see more.
            </p>
          </CardContent>
        </Card>
      )}

      {!loading && logs.length > 0 && (
        <div className="overflow-x-auto rounded-2xl border border-border bg-card/40 backdrop-blur">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="border-b border-border text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2">Action</th>
                <th className="px-3 py-2">Entity</th>
                <th className="px-3 py-2">Detail</th>
                <th className="px-3 py-2">User</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className="border-b border-border last:border-b-0">
                  <td className="whitespace-nowrap px-3 py-2 tabular-nums">{formatTs(l.timestamp)}</td>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center rounded-full border border-border bg-transparent px-2 py-0.5 text-[11px] font-medium">
                      {l.action}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <p className="font-mono text-[12px]">
                      {l.entityType}
                      {l.entityId ? ` · ${l.entityId.slice(0, 8)}` : ''}
                    </p>
                  </td>
                  <td className="max-w-[420px] break-words px-3 py-2 text-sm">
                    {l.detail ?? '—'}
                  </td>
                  <td className="px-3 py-2">
                    <p className="font-mono text-xs">{l.userId.slice(0, 12)}</p>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
